import { CheckIcon } from '@langchain/untitled-ui-icons';

import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Button, Text } from '@/design-system';
import { useOrganizationId } from '@/hooks/useSwr';
import { ChangePaymentPlanTierSchema } from '@/types/schema';
import { appOrganizationPath } from '@/utils/constants';
import { xCount } from '@/utils/stringUtils';

import { ErrorModal } from '../components/ErrorModal';
import {
  PAYMENT_PLAN_PRICING_MAP,
  PROGRAM_NAME,
  PROGRAM_SUBJECT,
} from '../utils/constants';
import { SuccessModal } from './SuccessModal';
import {
  DeploymentUsageBasedPlanFeatureTextWithTooltip,
  TraceUsageBasedPlanFeatureTextWithTooltip,
} from './UsageBasedPlanFeatureTextWithTooltip';
import { useBillingPlanStore } from './store';
import { isTierNeedingCard } from './utils/isTierNeedingCard';

export const StatusModal = ({
  tier,
  error,
  setError,
}: {
  tier: ChangePaymentPlanTierSchema;
  error: string | null;
  setError: (error: string | null) => void;
}) => {
  const tenantId = useOrganizationId();
  const isModalOpen = useBillingPlanStore((s) => s.isWelcomeMessageModalOpen);
  const setIsModalOpen = useBillingPlanStore(
    (s) => s.setIsWelcomeMessageModalOpen
  );

  const plusDescriptionItems = useMemo(
    () => [
      <Text>Up to {PAYMENT_PLAN_PRICING_MAP['plus'].maxSeats} seats</Text>,
      <TraceUsageBasedPlanFeatureTextWithTooltip planType="plus" />,
      <DeploymentUsageBasedPlanFeatureTextWithTooltip planType="plus" />,
      <Text>
        {xCount(
          'Dev deployment',
          PAYMENT_PLAN_PRICING_MAP['plus'].lgpDevDeployments || 0
        )}{' '}
        with usage included
      </Text>,
    ],
    []
  );
  if (error) {
    return <ErrorModal error={error} setError={setError} />;
  }

  if (tier === 'plus') {
    return (
      <SuccessModal
        isOpen={isModalOpen}
        setOpen={setIsModalOpen}
        title="Welcome to Plus!"
        description={
          <div className="flex flex-col items-start gap-3">
            <Text color="quaternary">You can now enjoy:</Text>
            <div className="flex flex-col gap-2">
              {plusDescriptionItems.map((t) => (
                <div className="flex gap-3">
                  <CheckIcon className="h-4 w-4 text-quaternary" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        }
        customCta={
          <Link to={`/${appOrganizationPath}/${tenantId}/settings/members`}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setIsModalOpen(false);
              }}
              size="md"
            >
              Invite members
            </Button>
          </Link>
        }
      />
    );
  }
  if (tier === 'developer') {
    return (
      <SuccessModal
        isOpen={isModalOpen}
        setOpen={setIsModalOpen}
        title="Card added to your Developer plan"
        description={
          <Text color="quaternary">
            You can now go beyond the 5,000 trace limit. Additional usage will
            be billed based on your consumption. Set usage limits to avoid
            accidental overspend.
          </Text>
        }
      />
    );
  }
  if (isTierNeedingCard(tier)) {
    return (
      <SuccessModal
        isOpen={isModalOpen}
        setOpen={setIsModalOpen}
        title={`Welcome to ${PROGRAM_NAME[tier]}!`}
        description={
          <Text color="quaternary">
            {PROGRAM_SUBJECT[tier]} are eligible for the program for{' '}
            {xCount('year', PAYMENT_PLAN_PRICING_MAP[tier].eligibilityYears)},
            after which you will be transitioned to our{' '}
            <Text weight="medium" as="span">
              Plus plan
            </Text>
            .
          </Text>
        }
      />
    );
  }
};
